import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import Utilities from "../../dependencies/custom/react-utilities";
import Settings from "../../dependencies/custom/settings";

const listFrom = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const query = (params = {}) => {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );
  return new URLSearchParams(cleaned).toString();
};

const TERMINAL_STATUSES = ["Completed", "Failed"];
const POLL_INTERVAL_MS = 4000;

const useAggregationRuns = () => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const pollTimerRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const request = useCallback(
    async (method, url, data, successMessage) => {
      try {
        const response = await Utilities.fetchWithAuth(
          method,
          url,
          data,
          navigate,
        );
        if (response?.ok) {
          if (successMessage) message.success(successMessage);
          return response.data ?? true;
        }
        Utilities.showError(
          response,
          response?.data?.detail || response?.msg || "Request failed",
        );
        return null;
      } catch (error) {
        console.error(error);
        message.error("Request failed");
        return null;
      }
    },
    [navigate],
  );

  const fetchRuns = useCallback(
    async (params = {}) => {
      setLoading(true);
      const qs = query(params);
      const data = await request(
        "GET",
        `${Settings.urls.academics.aggregationRuns}${qs ? `?${qs}` : ""}`,
        null,
      );
      const list = listFrom(data);
      setRuns(list);
      setLoading(false);
      return list;
    },
    [request],
  );

  const fetchRunDetail = useCallback(
    (id) =>
      request(
        "GET",
        Settings.urls.academics.aggregationRunDetail.replace(":id", id),
        null,
      ),
    [request],
  );

  const upsertRun = useCallback((run) => {
    if (!run) return;
    setRuns((prev) => {
      const exists = prev.some((r) => r.id === run.id);
      return exists
        ? prev.map((r) => (r.id === run.id ? run : r))
        : [run, ...prev];
    });
  }, []);

  const pollRun = useCallback(
    (id, onFinished) => {
      stopPolling();
      pollTimerRef.current = setInterval(async () => {
        const run = await fetchRunDetail(id);
        if (!run) {
          stopPolling();
          return;
        }
        upsertRun(run);
        if (TERMINAL_STATUSES.includes(run.status)) {
          stopPolling();
          if (run.status === "Completed") {
            message.success(
              `Aggregation run #${run.id} completed — ${run.recordsProcessed} records processed`,
            );
          } else {
            message.error(`Aggregation run #${run.id} failed`);
          }
          if (onFinished) onFinished(run);
        }
      }, POLL_INTERVAL_MS);
    },
    [fetchRunDetail, stopPolling, upsertRun],
  );

  const triggerRun = useCallback(
    async (data, onFinished) => {
      setLoading(true);
      const run = await request(
        "POST",
        Settings.urls.academics.aggregationRunTrigger,
        data,
        "Aggregation run queued",
      );
      setLoading(false);
      if (run?.id) {
        upsertRun(run);
        pollRun(run.id, onFinished);
      }
      return run;
    },
    [request, upsertRun, pollRun],
  );

  return {
    runs,
    loading,
    fetchRuns,
    fetchRunDetail,
    triggerRun,
    pollRun,
    stopPolling,
  };
};

export default useAggregationRuns;
